import { TriangleAlert, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * ErrorModal — displays API / form errors in a branded modal dialog.
 *
 * Props:
 *  - open        {boolean}  Whether the modal is visible
 *  - onClose     {function} Called when the user dismisses the modal
 *  - title       {string}   Headline text (default: "Etwas stimmt nicht")
 *  - message     {string}   Body / explanation text
 *  - errorCode   {string}   Optional error code shown below the message
 *  - buttonLabel {string}   CTA button label (default: "Zurück")
 */
const ErrorModal = ({
  open,
  onClose,
  title = "Etwas stimmt nicht",
  message = "Einige deiner Angaben scheinen falsch zu sein. Bitte gehe zurück und überprüfe sie.",
  errorCode,
  buttonLabel = "Zurück",
}) => {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent>
        {/* Close button */}
        <DialogClose className="absolute right-4 top-4 z-10 flex items-center justify-center size-8 rounded-[4px] bg-primary text-white hover:bg-black transition-colors cursor-pointer">
          <X className="size-4" />
          <span className="sr-only">Schließen</span>
        </DialogClose>

        <div className="flex flex-col gap-3 items-center p-4">
          {/* Warning icon */}
          <div className="flex items-center justify-center">
            <div className="rotate-[8deg]">
              <TriangleAlert className="size-12 text-primary" strokeWidth={1.5} />
            </div>
          </div>

          {/* Title */}
          <DialogTitle className="w-full text-center text-black text-[24px] leading-none tracking-[-0.48px] uppercase font-extrabold">
            {title}
          </DialogTitle>

          {/* Message + error code */}
          <DialogDescription asChild>
            <div className="flex flex-col gap-4 text-center w-full pb-2">
              <p className="text-[16px] leading-[1.4] text-black font-medium">
                {message}
              </p>
              {errorCode && (
                <p className="text-[12px] leading-[16px] text-black font-medium">
                  Error no. {errorCode}
                </p>
              )}
            </div>
          </DialogDescription>

          {/* Action button */}
          <Button
            type="button"
            className="w-full md:h-[54px] md:px-[20px] md:py-[13px] md:text-[24px] md:rounded-[14px]"
            size="sm"
            onClick={onClose}
          >
            {buttonLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ErrorModal;
